/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { ClusterQuiescence } from '../model/cluster-quiescence';
import { HttpClient } from '@angular/common/http';
import { ClusterUrlProviderService } from '../../core/services/cluster-url-provider.service';
import { clusterQuiescence, fetchQuiescenceState } from '../../configuration/+state/configuration.actions';
import { StompManagerService } from '../../core/services/stomp-manager/stomp-manager.service';
import { StompBaseConsumer } from '../../core/abstract/stomp-base-consumer';
import { Topic } from '../../user/model/multiple-subscriptions';

@Injectable({
    providedIn: 'root'
})
export class CallQuiesceService extends StompBaseConsumer implements OnDestroy {
    private readonly QUIESCENCE_STATE_URL = '/api/config/v1/quiescent-state';
    private readonly CTC_QUIESCE_URL = '/api/icc/v1/maintenance/quiesce';
    private readonly CTC_UNQUIESCE_URL = '/api/icc/v1/maintenance/unquiesce';

    constructor(
        private http: HttpClient,
        private clusterUrlProviderService: ClusterUrlProviderService,
        protected stompManager: StompManagerService,
        protected store: Store
    ) {
        super(stompManager, store);
    }

    protected handleQuiescenceMessage(quiescence: ClusterQuiescence) {
        console.log(`Quiescence message: ${JSON.stringify(quiescence)}`);
        this.store.dispatch(clusterQuiescence({ quiescence }));
    }

    protected getTopics(): Topic[] {
        return [Topic.QUIESCENCE];
    }

    protected getTopicHandlers(): { [p in Topic]?: (message: any) => void } {
        return {
            [Topic.QUIESCENCE]: this.handleQuiescenceMessage.bind(this),
        };
    }

    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Quiesce topic ${topic} subscription success: ${receiptId}`);
        this.store.dispatch(fetchQuiescenceState({ clusterName }));
    }

    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Quiesce topic ${topic} subscription end: ${receiptId}`);
    }

    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Quiesce topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }

    public requestCtcQuiescentState(clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, this.QUIESCENCE_STATE_URL);
        return this.http.get<ClusterQuiescence>(`${url}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public quiesce(clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, this.CTC_QUIESCE_URL);
        return this.http.post(url, {});
    }

    public unquiesce(clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, this.CTC_UNQUIESCE_URL);
        return this.http.post(url, {});
    }
}
